import {
    Center,
    Spinner,
    Flex,
    Container,
    Input,
    FormControl,
    FormLabel,
    FormErrorMessage,
    FormHelperText,
    Button,
    IconButton,
    InputGroup,
    InputRightAddon,
} from '@chakra-ui/react';

import { BigNumber, ethers } from 'ethers';

import { PaperPlane, PasteIcon } from '../../components/icons/icons';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import Tour from 'reactour';

import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import * as wActions from '../../redux/wallet/actionCreators';
import * as abActions from '../../redux/addressbook/actionCreators';
import * as txActions from '../../redux/transaction/actionCreators';
import PageBody from '../../components/pageBody/pageBody';

import SpeedSelection, {
    TransactionSpeed,
} from '../../components/speedSelection/speedSelection';
import GasPriceInput from '../../components/gasPriceInput/gasPriceInput';
import TransactionCost from '../../components/transactionCost/transactionCost';
import { gweiToEth, toast, infuraProviderByNetwork } from '../../imports/utils';
import EtherInput from '../../components/etherInput/etherInput';
import { AvailableNetworks } from '../../redux/common/types';
import Footer from '../../components/footer/footer';

type SendTransactionProps = {
    wallet?: any;
    loading: boolean;
    walletActions?: any;
    addressBookActions?: any;
    txActions?: any;
    addresses: Array<string>;
    network: AvailableNetworks;
};

const steps: any = [];

const GasPriceMap: { [key: string]: number } = {
    slow: 75,
    medium: 90,
    fast: 120,
};

interface SendTransactionParams {
    address: string;
}

const calcMax = async (
    address: string,
    gasPrice: number,
    network: AvailableNetworks,
) => {
    const provider = infuraProviderByNetwork(network);
    const balance = await provider.getBalance(address);
    const txCost = ethers.utils
        .parseUnits(gasPrice.toString(), 'gwei')
        .mul(21000);

    return balance.sub(txCost);
};

const SendTransaction = (props: SendTransactionProps) => {
    const history = useHistory();
    const { address } = useParams<SendTransactionParams>();
    const wallet = props.wallet;
    const loading = props.loading;
    const [isTourOpen, setIsTourOpen] = useState<boolean>(false);
    const [destinationAddress, setDestinationAddress] = useState<string>(
        address || '',
    );
    const [transactionSpeed, setTransactionSpeed] = useState<TransactionSpeed>(
        'slow',
    );
    const [gasPrice, setGasPrice] = useState<number>(GasPriceMap.slow);
    const [amount, setAmount] = useState<BigNumber>(BigNumber.from(0));
    const [max, setMax] = useState<BigNumber>();

    const isValidAddress = ethers.utils.isAddress(destinationAddress);
    const isValidAmount = max?.gte(amount) && amount.gt(0);

    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (!wallet) {
            history.replace('/wallet/create');
        }
    }, [wallet]);

    useEffect(() => {
        if (!isTourOpen) {
            // setShowFaucetModal(true);
            // show faucet modal
        }
    }, [isTourOpen]);

    useEffect(() => {
        calcMax(wallet.address, gasPrice, props.network).then(setMax);
    }, [gasPrice, props.network]);

    const handlePaste = useCallback(() => {
        navigator.clipboard
            .readText()
            .then(text => setDestinationAddress(text));
    }, [inputRef]);

    const handleReset = useCallback(() => setDestinationAddress(''), []);

    const handleInputChange = useCallback(
        e => setDestinationAddress(e.target.value),
        [],
    );

    const handleTransactionSpeed = useCallback(txSpeed => {
        setTransactionSpeed(txSpeed);
        setGasPrice(GasPriceMap[txSpeed]);
    }, []);

    const handleGasPriceChange = useCallback(gasPrice => {
        setGasPrice(gasPrice);
        setTransactionSpeed('custom');
    }, []);

    const handleMax = useCallback(async () => {
        const max = await calcMax(wallet.address, gasPrice, props.network);
        setAmount(max);
    }, [gasPrice, props.network]);

    const handleSend = useCallback(() => {
        if (!isValidAddress) {
            toast({
                title: 'Attenzione!',
                description: "L'indirizzo di destinazione è obbligatorio",
                status: 'error',
                duration: 3000,
                isClosable: true,
                position: 'top',
            });
            return;
        }

        if (!isValidAmount) {
            toast({
                title: 'Attenzione!',
                description: 'Fondi insufficenti',
                status: 'error',
                duration: 3000,
                isClosable: true,
                position: 'top',
            });
            return;
        }

        props.txActions.txSend(
            destinationAddress,
            amount,
            ethers.utils.parseUnits(gasPrice.toString(), 'gwei'),
        );
    }, [destinationAddress, amount, gasPrice, isValidAddress, isValidAmount]);

    if (loading) {
        return (
            <PageBody>
                <Center>
                    <Spinner color='blue.500' size='xl' />
                </Center>
            </PageBody>
        );
    }

    return (
        <Container maxW='6xl' h='100%'>
            <Flex direction='column' h='100%'>
                <Flex flex='1' justifyContent='center' direction='column'>
                    <Flex
                        flex='1'
                        justifyContent='center'
                        alignItems='center'
                        id='to'
                        pt={4}
                    >
                        <FormControl id='password'>
                            <FormLabel>Invia a:</FormLabel>
                            <InputGroup>
                                <Input
                                    ref={inputRef}
                                    type='text'
                                    value={destinationAddress}
                                    onChange={handleInputChange}
                                    placeholder='0x'
                                    isInvalid={!isValidAddress}
                                />
                                <InputRightAddon p={0}>
                                    <IconButton
                                        onClick={handlePaste}
                                        aria-label='Paste'
                                        icon={
                                            <PasteIcon
                                                position='absolute'
                                                right={2}
                                                top={3}
                                            />
                                        }
                                    />
                                </InputRightAddon>
                            </InputGroup>
                        </FormControl>
                    </Flex>
                    <Flex
                        flex='1'
                        justifyContent='center'
                        alignItems='center'
                        id='amount'
                        pt={4}
                    >
                        <FormControl id='amount'>
                            <FormLabel>Importo:</FormLabel>
                            <InputGroup>
                                <EtherInput
                                    onChange={val => setAmount(val)}
                                    value={amount}
                                    max={max}
                                />

                                <InputRightAddon p={0}>
                                    <Button onClick={handleMax}>max</Button>
                                </InputRightAddon>
                            </InputGroup>
                        </FormControl>
                    </Flex>
                    <Flex
                        flex='1'
                        justifyContent='center'
                        alignItems='center'
                        id='gasprice'
                        pt={4}
                    >
                        <FormControl id='gasprice'>
                            <FormLabel>Gas price:</FormLabel>
                            <InputGroup>
                                <GasPriceInput
                                    onChange={handleGasPriceChange}
                                    value={gasPrice}
                                />
                            </InputGroup>

                            {/* <FormHelperText>
                                Indirizzo di destinazione
                            </FormHelperText> */}
                        </FormControl>
                    </Flex>
                    <Flex flex='1' id='address' pt={4}>
                        <FormControl id='password'>
                            <FormLabel>Velocità transazione:</FormLabel>
                            <SpeedSelection
                                onChange={handleTransactionSpeed}
                                value={transactionSpeed}
                            />
                        </FormControl>
                    </Flex>
                    <Flex flex='1' id='address' pt={4}>
                        <TransactionCost gasPrice={gasPrice} gasLimit={21000} />
                    </Flex>
                </Flex>
                <Flex
                    h='80px'
                    w='100%'
                    justifyContent='center'
                    alignItems='center'
                >
                    <Button colorScheme='red' onClick={() => history.goBack()}>
                        Annulla
                    </Button>
                    <Button onClick={handleSend} ml={10}>
                        Invia
                    </Button>
                </Flex>
                <Footer />
            </Flex>
            <Tour
                steps={steps}
                isOpen={isTourOpen}
                onRequestClose={() => setIsTourOpen(false)}
            />
        </Container>
    );
};

const mapStateToProps = ({ wallet, addressbook, transaction, common }: any) => {
    return {
        wallet: wallet.wallet,
        loading: transaction.loading,
        addresses: addressbook.addresses,
        network: common.selectedNetwork,
    };
};

const mapDispatchToProps = (dispatch: any) => ({
    walletActions: bindActionCreators(wActions, dispatch),
    addressBookActions: bindActionCreators(abActions, dispatch),
    txActions: bindActionCreators(txActions, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(SendTransaction);
